import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Activity, RotateCcw, Save, Home, Truck, Users, Siren, TrafficCone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Descripteur des seuils : libelles et semantique de chaque critere
const SEUILS_CONFIG = [
    {
        dom: 'hebergement', label: 'Hébergement', Icon: Home,
        criteres: [
            { key: 'occupation_pct', label: 'Occupation globale des centres CARE (%)', hint: ['🟡 Jaune dès', '🟠 Orange dès', '🔴 Rouge dès'], suffix: '%' },
            { key: 'surcapacite', label: 'Centres en surcapacité', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: 'centres', note: 'Au-delà : rouge' },
        ]
    },
    {
        dom: 'moyens', label: 'Moyens & Véhicules', Icon: Truck,
        criteres: [
            { key: 'dispo_pct', label: 'Ressources disponibles (%)', hint: ['🟡 Jaune si <', '🟠 Orange si <', '🔴 Rouge si <'], suffix: '%', note: 'Valeurs décroissantes' },
            { key: 'vehicules_hs_pct', label: 'Véhicules en panne/maintenance (% de la flotte)', hint: ['🟠 Orange dès', '🔴 Rouge dès'], suffix: '%', note: '🟡 Jaune dès 1 véhicule' },
            { key: 'materiel_attente', label: 'Demandes de matériel en attente', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: 'demandes', note: 'Au-delà : rouge' },
        ]
    },
    {
        dom: 'personnel', label: 'Personnel', Icon: Users,
        criteres: [
            { key: 'fatigue_heures', label: 'Fatigue (heures d\'activité sans repos)', hint: ['🟡 Jaune dès', '🟠 Orange dès', '🔴 Rouge dès'], suffix: 'h', note: 'Orange aussi si plus de la moitié des actifs dépasse le seuil jaune' },
        ]
    },
    {
        dom: 'incidents', label: 'Incidents', Icon: Siren,
        criteres: [
            { key: 'actifs', label: 'Incidents actifs', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
            { key: 'critiques', label: 'Incidents de gravité critique', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
            { key: 'secours', label: 'Actions pompiers + police en cours', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
        ]
    },
    {
        dom: 'citoyen_voirie', label: 'Citoyen / Voirie', Icon: TrafficCone,
        criteres: [
            { key: 'zones', label: 'Zones dangereuses actives', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
            { key: 'restrictions', label: 'Restrictions routières actives', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
            { key: 'voirie', label: 'Interventions voirie en cours', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
            { key: 'urgentes', label: 'Demandes d\'action urgentes non traitées', hint: ['🟢 Vert jusqu\'à', '🟡 Jaune jusqu\'à', '🟠 Orange jusqu\'à'], suffix: '', note: 'Au-delà : rouge' },
        ]
    },
];

export default function SettingsPage() {
    const { token } = useAuth();
    const [seuils, setSeuils] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const fetchSeuils = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/dashboard/etat-general/seuils`, { headers: { Authorization: `Bearer ${token}` } });
            setSeuils(res.data.seuils);
        } catch {
            toast.error('Erreur de chargement des seuils');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchSeuils(); }, [fetchSeuils]);

    const updateValue = (dom, crit, idx, value) => {
        setSeuils(prev => {
            const next = { ...prev, [dom]: { ...prev[dom], [crit]: [...prev[dom][crit]] } };
            next[dom][crit][idx] = value === '' ? '' : Number(value);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/dashboard/etat-general/seuils`, { seuils }, { headers });
            toast.success('Seuils enregistrés — l\'État Général est recalculé immédiatement');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Réinitialiser tous les seuils aux valeurs par défaut ?')) return;
        try {
            const res = await axios.post(`${API}/dashboard/etat-general/seuils/reset`, {}, { headers });
            setSeuils(res.data.seuils);
            toast.success('Seuils réinitialisés');
        } catch {
            toast.error('Erreur');
        }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

    return (
        <div className="space-y-5 max-w-4xl" data-testid="settings-page">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="page-title">Paramètres</h1>
                        <p className="page-subtitle flex items-center gap-1.5"><Activity className="w-4 h-4" /> Seuils de l'indicateur État Général</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleReset} className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10" data-testid="seuils-reset-btn">
                            <RotateCcw className="w-4 h-4 mr-1.5" /> Valeurs par défaut
                        </Button>
                        <Button onClick={handleSave} disabled={saving} className="tactical-btn" data-testid="seuils-save-btn">
                            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </div>
            </div>

            <p className="text-sm text-zinc-400">
                Chaque domaine prend la <strong className="text-zinc-200">pire couleur</strong> de ses critères, et l'État Général la pire couleur des domaines.
                Les changements s'appliquent immédiatement à tous les tableaux de bord.
            </p>

            {SEUILS_CONFIG.map(({ dom, label, Icon, criteres }) => (
                <Card key={dom} className="content-card" data-testid={`seuils-${dom}`}>
                    <CardHeader className="card-header py-3">
                        <CardTitle className="card-title text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-blue-400" /> {label}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-4">
                        {criteres.map(({ key, label: cLabel, hint, suffix, note }) => (
                            <div key={key} className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-center">
                                <div>
                                    <p className="text-sm text-white">{cLabel}</p>
                                    {note && <p className="text-[11px] text-zinc-500">{note}</p>}
                                </div>
                                <div className="flex items-end gap-3 flex-wrap">
                                    {seuils[dom][key].map((val, idx) => (
                                        <div key={hint[idx]} className="w-[120px]">
                                            <p className="text-[10px] text-zinc-500 mb-1 whitespace-nowrap">{hint[idx]}</p>
                                            <div className="flex items-center gap-1">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    value={val}
                                                    onChange={(e) => updateValue(dom, key, idx, e.target.value)}
                                                    className="tactical-input h-8 text-sm"
                                                    data-testid={`seuil-${dom}-${key}-${idx}`}
                                                />
                                                {suffix && <span className="text-[10px] text-zinc-500">{suffix}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
